<template>
  <box v-if="attributes.length > 0" :title="t('Atributos do Laudo')">
    <template v-if="showEditToggle" #actions>
      <div
        class="attributes-edit-toggle"
        v-tooltip.top="editableLocked && lockedTooltip ? lockedTooltip : null"
        :data-testid="`${testIdPrefix}-toggle-wrapper`"
      >
        <InputToggle
          :elem-id="`${testIdPrefix}-toggle`"
          :data-testid="`${testIdPrefix}-toggle`"
          :label="t('Editar')"
          :model-value="editable"
          :disabled="editableLocked"
          @update:model-value="$emit('update:editable', $event)"
        />
      </div>
    </template>
    <div class="attributes-list">
      <div v-for="attribute in orderedAttributes" :key="attribute.so_ra_id" class="attribute-item">
        <!-- Text Attribute -->
        <input-text
          v-if="attribute.so_ra_type === 'text'"
          :data-testid="`${testIdPrefix}-${attribute.so_ra_id}`"
          :elem-id="`${testIdPrefix}_text_${attribute.so_ra_id}`"
          :label="attribute.so_ra_name"
          :placeholder="t('Digite um valor')"
          :disabled="!editable"
          :required="isRequired(attribute)"
          :model-value="attribute.value"
          @update:model-value="updateAttribute(attribute.so_ra_id, $event)"
        />

        <!-- Textarea Attribute -->
        <input-rich-text
          v-else-if="attribute.so_ra_type === 'textarea'"
          :data-testid="`${testIdPrefix}-${attribute.so_ra_id}`"
          :elem-id="`${testIdPrefix}_textarea_${attribute.so_ra_id}`"
          :label="attribute.so_ra_name"
          :disabled="!editable"
          :basic="true"
          :required="isRequired(attribute)"
          :model-value="attribute.value"
          @update:model-value="updateAttribute(attribute.so_ra_id, $event)"
        />

        <!-- Photo Attribute -->
        <div v-else-if="attribute.so_ra_type === 'photo'" class="photo-attribute">
          <label>
            <span v-if="isRequired(attribute)" class="required-indicator">*</span>
            {{ attribute.so_ra_name }}
          </label>
          <div v-if="attribute.value" class="photo-preview">
            <img :src="attribute.value" :alt="attribute.so_ra_name" class="preview-image" />
            <i v-if="uploadingAttributeId === attribute.so_ra_id" class="pi pi-spin pi-spinner uploading-spinner" />
            <Button
              v-if="editable && uploadingAttributeId !== attribute.so_ra_id"
              :data-testid="`${testIdPrefix}_${attribute.so_ra_id}-remove`"
              icon="pi pi-times"
              severity="danger"
              text
              size="small"
              class="photo-remove-btn"
              @click="updateAttribute(attribute.so_ra_id, null)"
            />
          </div>
          <div v-if="!attribute.value" class="photo-input-wrapper">
            <!-- Inputs de arquivo sempre no DOM (ocultos); os botões do popup os
                 acionam via for/id. Câmera usa capture="environment" (abre a câmera
                 direto no mobile); Galeria não usa capture (seletor do sistema,
                 inclusive no Android, que com capture iria direto pra câmera). -->
            <input
              :ref="(el) => registerPhotoInput(`${attribute.so_ra_id}-camera`, el)"
              :data-testid="`${testIdPrefix}_${attribute.so_ra_id}-camera`"
              type="file"
              accept="image/*"
              capture="environment"
              :disabled="!editable || uploadingAttributeId !== null"
              @change="(e) => handlePhotoChange(attribute, e)"
              class="hidden-file-input"
            />
            <input
              :ref="(el) => registerPhotoInput(`${attribute.so_ra_id}-gallery`, el)"
              :data-testid="`${testIdPrefix}_${attribute.so_ra_id}-file`"
              type="file"
              accept="image/*"
              :disabled="!editable || uploadingAttributeId !== null"
              @change="(e) => handlePhotoChange(attribute, e)"
              class="hidden-file-input"
            />

            <!-- Caixa clicável (visual idêntico ao anterior) -->
            <div
              class="photo-input"
              :class="{ 'disabled': !editable || uploadingAttributeId !== null }"
              :data-testid="`${testIdPrefix}_${attribute.so_ra_id}-open`"
              role="button"
              tabindex="0"
              @click="openPhotoPicker(attribute)"
              @keydown.enter.space.prevent="openPhotoPicker(attribute)"
            >
              <span class="photo-input-hint">{{ editable ? t('Clique para enviar uma foto') : t('Ative "Editar" para enviar uma foto') }}</span>
            </div>

            <!-- Popup: escolha entre câmera e galeria -->
            <template v-if="openPickerId === attribute.so_ra_id">
              <div class="photo-picker-backdrop" @click="openPickerId = null" />
              <div class="photo-picker-popup" :data-testid="`${testIdPrefix}_${attribute.so_ra_id}-picker`">
                <div
                  class="photo-picker-btn"
                  role="button"
                  tabindex="0"
                  :data-testid="`${testIdPrefix}_${attribute.so_ra_id}-picker-camera`"
                  @click="selectPhotoSource(attribute, 'camera')"
                  @keydown.enter.space.prevent="selectPhotoSource(attribute, 'camera')"
                >
                  <i class="pi pi-camera" />
                  <span>{{ t('Câmera') }}</span>
                </div>
                <div
                  class="photo-picker-btn"
                  role="button"
                  tabindex="0"
                  :data-testid="`${testIdPrefix}_${attribute.so_ra_id}-picker-gallery`"
                  @click="selectPhotoSource(attribute, 'gallery')"
                  @keydown.enter.space.prevent="selectPhotoSource(attribute, 'gallery')"
                >
                  <i class="pi pi-images" />
                  <span>{{ t('Galeria') }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Select Attribute -->
        <select-list
          v-else-if="attribute.so_ra_type === 'select'"
          :data-testid="`${testIdPrefix}-${attribute.so_ra_id}`"
          :elem-id="`${testIdPrefix}_select_${attribute.so_ra_id}`"
          :label="attribute.so_ra_name"
          :itens-select="attribute.so_ra_options || []"
          :disabled="!editable"
          :ng-required="isRequired(attribute)"
          :model-value="attribute.value"
          @update:model-value="updateAttribute(attribute.so_ra_id, $event)"
        />

        <!-- Checkbox Attribute -->
        <div v-else-if="attribute.so_ra_type === 'checkbox'" class="checkbox-attribute-item">
          <input-toggle
            :elem-id="`${testIdPrefix}_checkbox_${attribute.so_ra_id}`"
            :label="attribute.so_ra_name"
            :model-value="attribute.value === '1'"
            :disabled="!editable"
            :data-testid="`${testIdPrefix}-checkbox-${attribute.so_ra_id}`"
            @update:model-value="updateAttribute(attribute.so_ra_id, $event ? '1' : '0')"
          />
        </div>
      </div>
    </div>
  </box>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import InputToggle from '../../inputs/InputToggle.vue';
import { useSOLaudosComparison } from '../../composables/useSOLaudosComparison';
import { log, error as logError } from '../../utils/DebugLogger';
import type { ReportAttribute, ReportAttributeId as Id } from './reportAttribute';

interface Props {
  attributes: ReportAttribute[];
  orderItemId?: Id;
  editable?: boolean;
  showEditToggle?: boolean;
  editableLocked?: boolean;
  lockedTooltip?: string;
  testIdPrefix?: string;
}

const props = withDefaults(defineProps<Props>(), {
  orderItemId: undefined,
  editable: false,
  showEditToggle: true,
  editableLocked: false,
  lockedTooltip: '',
  testIdPrefix: 'so-report-attr'
});

const emit = defineEmits<{
  'update:attributes': [attributes: ReportAttribute[]];
  'update:editable': [editable: boolean];
}>();

const { t } = useI18n();

// O backend devolve os atributos por nome (ORDER BY so_ra_name), o que mistura
// fotos e defeitos. Exibe fotos primeiro e os defeitos (checkbox) logo abaixo,
// para comparar a imagem e marcar o defeito na sequência (NS 2026-0092); os
// demais tipos vêm depois. Sort estável: a ordem original vale dentro do grupo.
const TYPE_GROUP_ORDER: Record<string, number> = { photo: 0, checkbox: 1 };
const typeGroup = (attribute: ReportAttribute): number =>
  TYPE_GROUP_ORDER[attribute.so_ra_type ?? ''] ?? 2;

const orderedAttributes = computed((): ReportAttribute[] =>
  [...props.attributes].sort((a, b) => typeGroup(a) - typeGroup(b))
);

const isRequired = (attribute: ReportAttribute): boolean =>
  attribute.so_cra_required === 1 || (attribute as any).required === 1;

// Local working copy of the attributes. updateAttribute applies each child emit
// against THIS buffer rather than rebuilding off props.attributes. When several
// child selects emit their mount-time auto-select synchronously within the same
// tick, props has not yet re-rendered with the previous emit's value, so rebuilding
// off props reads a stale snapshot and only the last writer's value survives
// (last-writer-wins race). The buffer carries every prior synchronous emit, so each
// concurrent emit preserves the others.
const workingAttributes = ref<ReportAttribute[]>([...props.attributes]);

watch(
  () => props.attributes,
  (next) => {
    workingAttributes.value = [...next];
  }
);

const updateAttribute = (attributeId: Id, newValue: unknown): void => {
  const updatedAttributes: ReportAttribute[] = workingAttributes.value.map((attr) => {
    if (attr.so_ra_id === attributeId) {
      return { ...attr, value: newValue as string | null };
    }
    return attr;
  });
  workingAttributes.value = updatedAttributes;
  emit('update:attributes', updatedAttributes);
};

const { uploadAttributePhoto } = useSOLaudosComparison();

const uploadingAttributeId = ref<Id | null>(null);
const openPickerId = ref<Id | null>(null);
const photoInputRefs = new Map<string, HTMLInputElement>();

const registerPhotoInput = (key: string, el: unknown): void => {
  if (el) {
    photoInputRefs.set(key, el as HTMLInputElement);
  } else {
    photoInputRefs.delete(key);
  }
};

const openPhotoPicker = (attribute: ReportAttribute): void => {
  if (!props.editable || uploadingAttributeId.value !== null) return;
  openPickerId.value = attribute.so_ra_id;
};

const selectPhotoSource = (attribute: ReportAttribute, kind: 'camera' | 'gallery'): void => {
  // aciona o input oculto correspondente (câmera usa capture; galeria não)
  photoInputRefs.get(`${attribute.so_ra_id}-${kind}`)?.click();
  openPickerId.value = null;
};

const handlePhotoChange = async (attribute: ReportAttribute, event: Event): Promise<void> => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  openPickerId.value = null;

  log('SOReportAttributesList', 'handlePhotoChange', 'Uploading photo for attribute', { attributeId: attribute.so_ra_id });

  const previewUrl = URL.createObjectURL(file);
  updateAttribute(attribute.so_ra_id, previewUrl);

  uploadingAttributeId.value = attribute.so_ra_id;
  try {
    const url = await uploadAttributePhoto(file, props.orderItemId!, attribute.so_ra_id);
    URL.revokeObjectURL(previewUrl);
    updateAttribute(attribute.so_ra_id, url);
    log('SOReportAttributesList', 'handlePhotoChange', 'Photo uploaded', { url });
  } catch (err) {
    logError('SOReportAttributesList', 'handlePhotoChange', 'Upload failed', err);
    updateAttribute(attribute.so_ra_id, null);
  } finally {
    uploadingAttributeId.value = null;
  }
};
</script>

<style scoped>
.attributes-edit-toggle {
  display: flex;
  align-items: center;
}

.attributes-edit-toggle :deep(.toggle-container) {
  margin: 0;
  gap: 8px;
}

.attributes-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attribute-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
}

.photo-attribute {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.photo-attribute label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.photo-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  border: 1px solid var(--p-surface-200);
  border-radius: 6px;
  overflow: hidden;
  background: var(--p-surface-50);
}

.uploading-spinner {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 1.2rem;
  color: var(--p-primary-color);
}

.preview-image {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 4px;
}

.photo-remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.photo-remove-btn:hover {
  opacity: 1;
}

.photo-input-wrapper {
  position: relative;
  display: inline-block;
}

.photo-input {
  display: inline-block;
  padding: 12px 16px;
  border: 2px dashed var(--p-surface-300);
  border-radius: 6px;
  background: var(--p-surface-50);
  cursor: pointer;
  transition: all 0.2s ease;
}

.photo-input:hover {
  border-color: var(--p-primary-color);
  background: var(--p-surface-100);
}

.photo-input.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.photo-input.disabled:hover {
  border-color: var(--p-surface-300);
  background: var(--p-surface-50);
}

.photo-input-hint {
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.hidden-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.photo-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.photo-picker-popup {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 190px;
  padding: 6px;
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
}

.photo-picker-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  color: var(--p-text-color);
  font-size: 0.92rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.photo-picker-btn:hover {
  background: var(--p-surface-100);
}

.photo-picker-btn i {
  font-size: 1.05rem;
  color: var(--p-primary-color);
}

.required-indicator {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.checkbox-attribute-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

/* O InputToggle reserva 20% (mín. 120px) para o rótulo, sem quebra de linha e
   alinhado à direita: rótulos de defeito mais longos invadiam o toggle e ficavam
   cortados (NS 2026-0092). Aqui o rótulo ocupa o espaço livre e quebra linha, e o
   toggle fica à direita, em linha, em qualquer largura. */
.checkbox-attribute-item :deep(.toggle-container) {
  flex: 1 1 auto;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  min-width: 0;
}

.checkbox-attribute-item :deep(.toggle-label) {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.checkbox-attribute-item :deep(.field-label) {
  white-space: normal;
  overflow-wrap: anywhere;
}

.checkbox-attribute-item :deep(.toggle-content) {
  flex: 0 0 auto;
  width: auto;
}
</style>
